import { CheckIcon, CloseIcon, RestoreIcon } from './ui/icons';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { prepareItemImage } from '../services/imageProcessing';
import ReactCrop, { Crop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { useFeedback } from '../features/feedback/FeedbackProvider';

interface ImageCropModalProps {
  open: boolean;
  imageSrc: string;
  source?: Blob;
  initialState?: CropViewState;
  onConfirm: (blob: Blob, state: CropViewState) => void;
  onCancel: () => void;
}

interface Point {
  x: number;
  y: number;
}

export interface CropViewState {
  crop: Crop;
  viewScale: number;
  viewOffset: Point;
}

const MIN_VIEW_SCALE = 0.25;
const DEFAULT_VIEW_SCALE = 1;
const MAX_VIEW_SCALE = 4;

const distanceBetween = (first: Point, second: Point) =>
  Math.hypot(second.x - first.x, second.y - first.y);

const squareImageCrop = (width: number, height: number): Crop => {
  const size = Math.min(width, height);
  return {
    unit: '%',
    x: ((width - size) / width) * 50,
    y: ((height - size) / height) * 50,
    width: (size / width) * 100,
    height: (size / height) * 100,
  };
};

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  imageSrc,
  source,
  initialState,
  onConfirm,
  onCancel,
}) => {
  const { notify } = useFeedback();
  const processingRef = useRef<AbortController | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const cropAreaRef = useRef<HTMLDivElement>(null);
  const outsidePointers = useRef(new Map<number, Point>());
  const lastPanPoint = useRef<Point | null>(null);
  const lastPinchDistance = useRef<number | null>(null);
  const viewScaleRef = useRef(DEFAULT_VIEW_SCALE);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cropAreaHeight, setCropAreaHeight] = useState(0);
  const [viewOffset, setViewOffset] = useState<Point>({ x: 0, y: 0 });
  const [viewScale, setViewScale] = useState(DEFAULT_VIEW_SCALE);
  const [isPanning, setIsPanning] = useState(false);

  useLayoutEffect(() => {
    const area = cropAreaRef.current;
    if (!area) return;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      if (height && height > 0) setCropAreaHeight(height);
    });
    observer.observe(area);
    setCropAreaHeight(area.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [open]);

  const onImageLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = event.currentTarget;
      const initialCrop = initialState?.crop ?? squareImageCrop(width, height);
      setCrop(initialCrop);
      setCompletedCrop(initialCrop);
    },
    [initialState],
  );

  const handleConfirmCrop = async () => {
    if (!imgRef.current || !completedCrop) return;
    const image = imgRef.current;
    setIsProcessing(true);
    try {
      const controller = new AbortController();
      processingRef.current = controller;
      const original =
        source ??
        (await fetch(imageSrc, { signal: controller.signal }).then(
          (response) => {
            if (!response.ok) throw new Error('Image could not be loaded');
            return response.blob();
          },
        ));
      if (!original) throw new Error('Image could not be loaded');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const pixelCrop = convertToPixelCrop(
        completedCrop,
        image.width,
        image.height,
      );
      const x = Math.round(pixelCrop.x * scaleX);
      const y = Math.round(pixelCrop.y * scaleY);
      const size = Math.min(
        Math.round(pixelCrop.width * scaleX),
        Math.round(pixelCrop.height * scaleY),
        image.naturalWidth - x,
        image.naturalHeight - y,
      );
      const result = await prepareItemImage(original, {
        crop: { x, y, size },
        signal: controller.signal,
      });
      if (!controller.signal.aborted)
        onConfirm(result.file, { crop: completedCrop, viewScale, viewOffset });
    } catch {
      if (processingRef.current?.signal.aborted) return;
      notify('Failed to crop image. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUseFullImage = () => {
    if (!imgRef.current) return;
    const { width, height } = imgRef.current;
    const fullCrop = squareImageCrop(width, height);
    setCrop(fullCrop);
    setCompletedCrop(fullCrop);
    setViewOffset({ x: 0, y: 0 });
    viewScaleRef.current = DEFAULT_VIEW_SCALE;
    setViewScale(DEFAULT_VIEW_SCALE);
  };

  React.useEffect(() => {
    if (!open) return;
    return () => processingRef.current?.abort();
  }, [open, imageSrc]);

  React.useEffect(() => {
    if (!open) return;
    setCrop(undefined);
    setCompletedCrop(undefined);
    setIsProcessing(false);
    setViewOffset(initialState?.viewOffset ?? { x: 0, y: 0 });
    viewScaleRef.current = initialState?.viewScale ?? DEFAULT_VIEW_SCALE;
    setViewScale(viewScaleRef.current);
    setIsPanning(false);
    outsidePointers.current.clear();
    lastPanPoint.current = null;
    lastPinchDistance.current = null;
  }, [open, imageSrc, initialState]);

  const applyViewScale = (nextScale: number) => {
    const boundedScale = Math.min(
      MAX_VIEW_SCALE,
      Math.max(MIN_VIEW_SCALE, nextScale),
    );
    viewScaleRef.current = boundedScale;
    setViewScale(boundedScale);
  };

  const cropAreaCleanupRef = useRef<(() => void) | null>(null);
  const handleCropAreaRef = useCallback((node: HTMLDivElement | null) => {
    cropAreaCleanupRef.current?.();
    cropAreaCleanupRef.current = null;
    cropAreaRef.current = node;
    if (!node) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      applyViewScale(viewScaleRef.current * (event.deltaY < 0 ? 1.2 : 1 / 1.2));
    };

    node.addEventListener('wheel', handleWheel, { passive: false });
    cropAreaCleanupRef.current = () => {
      node.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handlePanStart = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as Element;
    if (target.closest('.ReactCrop__crop-selection')) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = { x: event.clientX, y: event.clientY };
    outsidePointers.current.set(event.pointerId, point);
    if (outsidePointers.current.size === 2) {
      const [first, second] = Array.from(outsidePointers.current.values());
      lastPinchDistance.current = distanceBetween(first, second);
      lastPanPoint.current = null;
    } else {
      lastPanPoint.current = point;
    }
    setIsPanning(true);
  };

  const handlePanMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!outsidePointers.current.has(event.pointerId)) return;
    const nextPoint = { x: event.clientX, y: event.clientY };
    outsidePointers.current.set(event.pointerId, nextPoint);

    if (outsidePointers.current.size === 2) {
      const [first, second] = Array.from(outsidePointers.current.values());
      const nextDistance = distanceBetween(first, second);
      if (lastPinchDistance.current) {
        applyViewScale(
          viewScaleRef.current * (nextDistance / lastPinchDistance.current),
        );
      }
      lastPinchDistance.current = nextDistance;
      return;
    }

    if (!lastPanPoint.current) {
      lastPanPoint.current = nextPoint;
      return;
    }
    const previousPoint = lastPanPoint.current;
    setViewOffset((current) => ({
      x: current.x + nextPoint.x - previousPoint.x,
      y: current.y + nextPoint.y - previousPoint.y,
    }));
    lastPanPoint.current = nextPoint;
  };

  const handlePanEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!outsidePointers.current.has(event.pointerId)) return;
    outsidePointers.current.delete(event.pointerId);
    lastPinchDistance.current = null;
    lastPanPoint.current =
      outsidePointers.current.size === 1
        ? Array.from(outsidePointers.current.values())[0]
        : null;
    if (outsidePointers.current.size === 0) setIsPanning(false);
  };

  const imgMaxHeight =
    cropAreaHeight > 0 ? `${Math.max(1, cropAreaHeight - 16)}px` : '100%';

  return (
    <Modal
      open={open}
      onClose={isProcessing ? undefined : onCancel}
      disableEscapeKeyDown
    >
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          color: 'text.primary',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', textAlign: 'right' }}
          >
            Resize or move the crop · drag outside to pan · scroll or pinch to
            zoom · {Math.round(viewScale * 100)}%
          </Typography>
        </Box>

        <Box
          ref={handleCropAreaRef}
          data-testid="crop-pan-area"
          onPointerDown={handlePanStart}
          onPointerMove={handlePanMove}
          onPointerUp={handlePanEnd}
          onPointerCancel={handlePanEnd}
          sx={{
            width: '100%',
            flex: 1,
            boxSizing: 'border-box',
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            p: 1,
            touchAction: 'none',
            cursor: isPanning ? 'grabbing' : 'grab',
          }}
        >
          <Box
            data-testid="crop-view"
            sx={{
              maxWidth: '100%',
              minWidth: 0,
              transform: `translate(${viewOffset.x}px, ${viewOffset.y}px) scale(${viewScale})`,
              transformOrigin: 'center',
              touchAction: 'none',
            }}
          >
            <ReactCrop
              crop={crop}
              aspect={1}
              keepSelection
              onChange={(_nextCrop, percentageCrop) => setCrop(percentageCrop)}
              onComplete={(_nextCrop, percentageCrop) => {
                if (imgRef.current) {
                  setCompletedCrop(percentageCrop);
                }
              }}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                touchAction: 'none',
              }}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                crossOrigin="anonymous"
                alt="Crop preview"
                onLoad={onImageLoad}
                draggable={false}
                style={{
                  maxWidth: '100%',
                  maxHeight: imgMaxHeight,
                  display: 'block',
                  touchAction: 'none',
                  userSelect: 'none',
                }}
              />
            </ReactCrop>
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            gap: 1,
            p: 2,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isProcessing}
            startIcon={<CloseIcon />}
            sx={{ flexShrink: 0, minWidth: 90 }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleUseFullImage}
            disabled={isProcessing || !imgRef.current}
            startIcon={<RestoreIcon />}
            sx={{ flex: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCrop}
            disabled={
              isProcessing || !completedCrop || completedCrop.width === 0
            }
            startIcon={
              isProcessing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckIcon />
              )
            }
            sx={{ flex: 1 }}
          >
            {isProcessing ? 'Processing…' : 'Confirm'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImageCropModal;
